import { Divider, Flex } from "antd"
import { colors, spacings } from "themes/variables"
import IconWithBg from "ui/IconWithBg"
import { Text } from "ui/Typography"
import styles from "../index.module.css"
import moment from "moment"
import { useState } from "react"
import PropTypes from "prop-types"
import { getEventBorderColor, getEventColor, getJournalIcon, hasEventConflict } from "pagesV2/pro/Calendar/utils"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import EventBlock from "../EventBlock"
import Journal from "pagesV2/pro/Calendar/Modals/Journal"
import { useCalendarJournalNoteDates } from "pagesV2/pro/Calendar/hooks/useCalendarSelectors"
import { useTimeOffForDate } from "pagesV2/pro/Calendar/hooks/useTimeOff"
import useActiveGuide from "hooks/useActiveClient"

const DayGrid = ({ date, hours, events }) => {
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const displayDate = date ? moment(date).format("dddd, D MMMM") : ""
  const journalNoteDates = useCalendarJournalNoteDates()
  const [active] = useActiveGuide()

  const handleJournalClick = () => {
    setIsJournalModalOpen(true)
  }

  const currentDay = moment(date).format("YYYY-MM-DD")
  // Convert event times to 24-hour format for matching
  const normalizedDayEvents = events.map((ev) => {
    const startMoment = ev.start ? moment(ev.start, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    const endMoment = ev.end ? moment(ev.end, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    return {
      ...ev,
      startMoment,
      endMoment,
      startHour: startMoment?.isValid() ? startMoment.hour() : null,
      endHour: endMoment?.isValid() ? endMoment.hour() : null,
      startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
      endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end
    }
  })

  const dayEvents = normalizedDayEvents.filter((ev) => moment(ev.date).format("YYYY-MM-DD") === currentDay)

  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }

  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
  }

  // Centralized time-off info via hook
  const timeOffForDay = useTimeOffForDate(date)

  return (
    <>
      <Flex vertical>
        <Flex gap={spacings.XS} align="center" className="pt-m pl-m">
          <Text color={colors.textSecondaryDark}>{displayDate}</Text>
          <IconWithBg
            icon={getJournalIcon(journalNoteDates, currentDay)}
            onClick={active ? handleJournalClick : undefined}
            disabled={!active}
          />
        </Flex>
        <Divider />
        <div className={styles.timeGridRows} style={{ gridTemplateRows: `repeat(${hours.length}, 1fr)` }}>
          {hours.map((hour) => {
            // Check if an event starts at this hour
            const event = dayEvents.find((ev) => ev.startHour === hour)
            // Determine if this hour is in the past (whole past days or earlier hours today)
            const now = moment()
            const dayMoment = moment(date)
            const isPastHour = dayMoment.isBefore(now, "day") || (dayMoment.isSame(now, "day") && Number(hour) < now.hour())

            // Check if this hour is inside an event block (but not the start)
            const isInEvent = dayEvents.some((ev) => ev.startHour < hour && ev.endHour > hour)
            if (isInEvent && !event) {
              // Render an empty row for hours inside an event block, but not at the start
              return (
                <div key={hour} className={styles.timeGridRow}>
                  <div className={styles.timeLabel}>
                    <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                  </div>
                  <div className={styles.timeDivider} />
                  <div className={`${isPastHour ? styles.pastCalendarCell : ""}`} />
                </div>
              )
            }
            return (
              <div key={hour} className={styles.timeGridRow}>
                <div className={styles.timeLabel}>
                  <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                </div>
                <div className={styles.timeDivider} />
                <div className={`${styles.eventCell} ${isPastHour ? styles.pastCalendarCell : ""}`}>
                  {(timeOffForDay?.ranges || []).map((toff) => {
                    const start = toff.start.hour() + toff.start.minute() / 60
                    const end = toff.end.hour() + toff.end.minute() / 60
                    if (start < hour + 1 && end > hour) {
                      const top = Math.max(0, (start - hour) * 100) // percent
                      const height = (Math.min(end, hour + 1) - Math.max(start, hour)) * 100
                      return (
                        <div
                          key={`${currentDay}-${toff.start.format("HH:mm")}-${toff.end.format("HH:mm")}`}
                          className={styles.timeOffOverlay}
                          style={{ top: `${top}%`, height: `${height}%` }}
                        />
                      )
                    }
                    return null
                  })}
                  {dayEvents
                    .filter((ev) => ev.startHour !== null && ev.endHour !== null && ev.startHour <= hour && ev.endHour > hour)
                    .map((ev) => {
                      const { startMoment, endMoment } = ev
                      const eventStartHour = startMoment?.hour() ?? 0
                      const eventStartMin = startMoment?.minute() ?? 0
                      const eventEndHour = endMoment?.hour() ?? 0
                      const eventEndMin = endMoment?.minute() ?? 0
                      // Calculate top offset as a percentage of the hour cell
                      const topPct = (eventStartHour + eventStartMin / 60 - hour) * 100
                      const durationHours = eventEndHour + eventEndMin / 60 - (eventStartHour + eventStartMin / 60)
                      const heightPct = durationHours * 100
                      // Find how many events share this exact start time
                      const sameStartEvents = dayEvents.filter((e2) => e2.start === ev.start)
                      const stackIdx = sameStartEvents.findIndex(
                        (e2) => (e2.id || e2.title + e2.start + e2.end) === (ev.id || ev.title + ev.start + ev.end)
                      )
                      const stackOffset = stackIdx * 12 // px offset for stacking
                      const eventColor = getEventColor(ev.payment_status)
                      const eventBorderColor = getEventBorderColor(ev.payment_status)
                      const conflict = hasEventConflict(ev.startMoment, ev.endMoment, timeOffForDay)
                      return (
                        <EventBlock
                          key={ev.id || ev.title + ev.start + ev.end}
                          event={ev}
                          stackOffset={stackOffset}
                          topPct={topPct}
                          heightPct={heightPct}
                          stackIdx={stackIdx}
                          eventColor={eventColor}
                          eventBorderColor={eventBorderColor}
                          conflict={conflict}
                          setIsBookingModalOpen={setIsBookingModalOpen}
                          setSelectedBookingId={setSelectedBookingId}
                        />
                      )
                    })}
                </div>
              </div>
            )
          })}
        </div>
      </Flex>
      {isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={date}
          startDate={date}
          endDate={date}
        />
      )}
      {isBookingModalOpen && (
        <BookingDetailsModal open={isBookingModalOpen} handleOnCancel={handleBookingCancel} bookingUuid={selectedBookingId} />
      )}
    </>
  )
}

DayGrid.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired
}

export default DayGrid
